import { useState, type ReactNode } from 'react'
import { useAuth } from '@/auth/context'
import { displayName } from '@/auth/authApi'
import { Input } from '@/ui'
import { cn } from '@/lib/cn'
import { flagState } from '../../model/labReport'
import type { LabRow } from '../../api/quality'
import { BoardTable, Pill, type BoardColumn } from '../../components/board'

/** "Meera Joshi · Quality · release role" — the signer, named. */
export function Signer({ children }: { children?: ReactNode }) {
  const { user, roles } = useAuth()
  const quality = roles.includes('quality_release')
  return (
    <div className="flex flex-col">
      <b className="text-[17px]">{user ? displayName(user) : '—'}</b>
      <span className="text-[14px] text-muted">
        {quality ? 'Quality · release role' : 'Office'}
      </span>
      {children}
    </div>
  )
}

/** The tick and the password: every signature re-asks the login password (5-minute token). */
export function SignFields({
  attested,
  onAttested,
  attestLabel,
  password,
  onPassword,
}: {
  attested?: boolean
  onAttested?: (v: boolean) => void
  attestLabel?: ReactNode
  password: string
  onPassword: (v: string) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      {onAttested && (
        <label className="flex items-start gap-2 text-[15px]">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-ink"
            checked={Boolean(attested)}
            onChange={(e) => onAttested(e.target.checked)}
          />
          <span>{attestLabel}</span>
        </label>
      )}
      <label className="flex flex-col gap-1 text-[15px]">
        <span className="font-bold">Your password</span>
        <Input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => onPassword(e.target.value)}
        />
      </label>
    </div>
  )
}

/** Analyte rows with their flag; "not detected" always with its LOQ. */
export function LabRowsTable({
  rows,
  className,
  first = 8,
}: {
  rows: LabRow[]
  className?: string
  first?: number
}) {
  const [all, setAll] = useState(false)
  // Rows that are not Pass always show; the rest fold after the first few.
  const notPass = rows.filter((r) => r.flag !== 'PASS')
  const pass = rows.filter((r) => r.flag === 'PASS')
  const shown = all ? rows : [...notPass, ...pass.slice(0, Math.max(0, first - notPass.length))]
  const hidden = rows.length - shown.length
  const columns: BoardColumn<LabRow & { key: string }>[] = [
    {
      id: 'analyte',
      header: 'Analyte',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <b>{r.analyte}</b>
          <span className="text-[13px] text-muted">{r.panel}</span>
        </span>
      ),
    },
    { id: 'result', header: 'Result', cell: (r) => (r.value === null ? 'Not detected' : r.value) },
    { id: 'unit', header: 'Unit', hideBelow: 'sm', cell: (r) => r.uom },
    { id: 'loq', header: 'LOQ', align: 'right', cell: (r) => r.loq },
    {
      id: 'limit',
      header: 'Limit',
      align: 'right',
      hideBelow: 'sm',
      cell: (r) => r.limitValue ?? '—',
    },
    {
      id: 'flag',
      header: <span className="sr-only">Flag</span>,
      cell: (r) => {
        const s = flagState(r.flag)
        return <Pill tone={s.tone}>{s.text}</Pill>
      },
    },
  ]
  return (
    <div className={cn(className)}>
      <BoardTable
        caption="Lab rows"
        columns={columns}
        rows={shown.map((r, i) => ({ ...r, key: `${r.panel}-${r.analyte}-${i}` }))}
        getRowId={(r) => r.key}
        empty="No rows."
      />
      {hidden > 0 && (
        <button
          type="button"
          className="mt-1.5 text-[14px] font-bold underline"
          onClick={() => setAll(true)}
        >
          + {hidden} more rows, all pass · Show all
        </button>
      )}
    </div>
  )
}
